import React, { useEffect, useState, useCallback, useRef } from 'react'
import { ethers } from 'ethers'
import { Button } from '../../components/Button'
import { formatDate } from '../../utils/formatDate'
import Spinner from '../Spinner'
import { shortenAddress } from '../../utils'
import networks from '../../networks.json'
import { CHAIN_ID } from '../../constants'
import { getMetarunToken, getFixedStaking } from '../../api/contracts'

import {
  Modal,
  ModalContainer,
  ModalCloseButton,
  ModalForm,
  ModalButtonContainer,
  ModalInput,
  ModalInputContainer,
  ModalInputLabel,
  ModalTitle,
  ModalText,
  ModalTextWrapper,
  ModalError,
  StakeMaxButtonWrapper,
  ModalTxText,
  ModalTxLink,
  ModalSpinnerWrapper,
  ModalButtonWrapper,
  ModalTextDescription,
} from './StakingModal.styled'

export default function StakingModal({
  isOpen,
  onClose,
  mrunBalance,
  allowance,
  setAllowance,
  yieldRate,
  mrunPerCharacter,
  stakeDuration,
  name,
  forceUpdate,
  setCardIsLoading,
  userAddress,
  contractAddress,
  earlyUnstakeFee,
}) {
  const modal = useRef(null)

  const [amount, setAmount] = useState('1')
  const [inputValidity, setInputValidity] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [txHash, setTxHash] = useState()
  const [isApproveProcess, setIsApproveProcess] = useState(false)

  const amountHandler = e => {
    e.preventDefault()
    setAmount(e.target.value)
  }

  function submitHandler(e) {
    e.preventDefault()
    setIsLoading(true)

    if (
      Number(ethers.utils.parseEther(amount.toString())) >
      Number(allowance) - 100
    ) {
      approveHandler()
    } else {
      stakeHandler()
    }
  }

  const approveHandler = () => {
    setIsApproveProcess(true)
    setIsLoading(true)
    const MetarunToken = getMetarunToken()
    MetarunToken.approve(contractAddress, ethers.constants.MaxUint256)
      .then(tx => {
        setIsApproveProcess(false)
        setTxHash(tx.hash)
        tx.wait()
          .then(() => {
            MetarunToken.allowance(userAddress, contractAddress).then(res => {
              setAllowance(res.toString())
              setIsLoading(false)
              setTxHash()
              stakeHandler()
            })
          })
          .catch(err => {
            setIsApproveProcess(false)
            setTxHash()
            setIsLoading(false)
            console.log(err)
          })
      })
      .catch(err => {
        setIsApproveProcess(false)
        setTxHash()
        setIsLoading(false)
        console.log(err)
      })
  }

  const stakeHandler = () => {
    setIsLoading(true)
    const FixedStaking = getFixedStaking(contractAddress)
    if (inputValidity) {
      FixedStaking.stake(ethers.utils.parseEther(amount.toString()), {
        gasLimit: 500000,
      })
        .then(tx => {
          setTxHash(tx.hash)
          tx.wait()
            .then(() => {
              setIsLoading(false)
              setTxHash()
              onClose()
              setCardIsLoading(true)
              forceUpdate()
            })
            .catch(err => {
              setIsLoading(false)
              setTxHash()
              console.log(err)
            })
        })
        .catch(err => {
          setIsLoading(false)
          setTxHash()
          console.log(err)
        })
    }
  }

  const handleOverlayClose = useCallback(event => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }, [])

  useEffect(() => {
    if (modal.current) {
      modal.current.addEventListener('click', handleOverlayClose)
    }
    return () => {
      if (modal.current) {
        modal.current.removeEventListener('click', handleOverlayClose)
      }
    }
  }, [handleOverlayClose])

  useEffect(() => {
    const stakeAmountNumber = amount ? Number(amount.replaceAll(',', '.')) : 0

    if (isNaN(stakeAmountNumber) || stakeAmountNumber <= 0) {
      setInputValidity(false)
    } else {
      setInputValidity(true)
    }
  }, [amount, allowance])

  return (
    <Modal isOpen={isOpen} ref={modal}>
      <ModalContainer>
        <ModalButtonContainer
          onClick={() => {
            onClose()
          }}
        >
          <ModalCloseButton
            src={require('../../images/Close.svg').default}
            alt='close'
            width='14'
            height='14'
          ></ModalCloseButton>
        </ModalButtonContainer>
        <ModalTitle>Stake MRUN tokens</ModalTitle>
        {isLoading ? (
          <>
            {!isApproveProcess && (
              <ModalSpinnerWrapper>
                <Spinner size='80px' />
              </ModalSpinnerWrapper>
            )}
            {!txHash && isApproveProcess ? (
              <>
                <ModalText>
                  To approve Staking to receive payment in MRUN, you must first
                  complete a free (excluding Gas) transaction. Please confirm
                  this in your wallet and keep this modal open!
                </ModalText>
                <ModalText>
                  You may notice a very large number being requested for
                  approval. This is simply the maximum amount, meaning you’ll
                  never have to do this approval again. It also doesn’t give
                  permission for Metarun to take this amount from you. Staking
                  amount is all that will be charged.
                </ModalText>
              </>
            ) : !txHash ? (
              <ModalTxText>Await confirmation</ModalTxText>
            ) : (
              <ModalTxLink
                target='_blank'
                href={`${networks[CHAIN_ID].params.blockExplorerUrls}tx/${txHash}`}
              >
                TxHash: {shortenAddress(txHash)}
              </ModalTxLink>
            )}
          </>
        ) : (
          <ModalForm>
            <ModalText>
              Wallet Balance:{' '}
              <ModalTextWrapper>
                {Number(mrunBalance).toFixed(2)} MRUN
              </ModalTextWrapper>
            </ModalText>

            <ModalInputContainer>
              <ModalInputLabel>Amount</ModalInputLabel>
              <ModalInput
                invalid={Number(amount) > Number(mrunBalance) || !inputValidity}
                value={amount}
                placeholder='0.1'
                onChange={amountHandler}
              />
              {!inputValidity ? (
                <ModalError>Value must be a number greater than 0</ModalError>
              ) : (
                Number(amount) > Number(mrunBalance) && (
                  <ModalError>Not enough MRUN balance</ModalError>
                )
              )}
              <StakeMaxButtonWrapper>
                <Button
                  variant='modal'
                  invalid={
                    !inputValidity || Number(amount) > Number(mrunBalance)
                  }
                  onClick={e => {
                    e.preventDefault()
                    setAmount(mrunBalance)
                  }}
                >
                  Max
                </Button>
              </StakeMaxButtonWrapper>
            </ModalInputContainer>

            <ModalText>
              <ModalTextDescription>Maturity date:</ModalTextDescription>
              <ModalTextWrapper>
                {formatDate(Date.now() / 1000 + stakeDuration, true)}
              </ModalTextWrapper>
            </ModalText>
            <ModalText style={{ color: 'red' }}>
              <ModalTextDescription>
                Early unstake penalty:
              </ModalTextDescription>
              <ModalTextWrapper>
                {Math.floor(
                  Number(amount) /
                    Number(ethers.utils.formatEther(mrunPerCharacter))
                ) > 0 &&
                Math.floor(
                  Number(amount) / ethers.utils.formatEther(mrunPerCharacter)
                ) !== Infinity
                  ? earlyUnstakeFee
                  : Number(earlyUnstakeFee) / 4}
                %
              </ModalTextWrapper>
            </ModalText>
            <ModalText>
              <ModalTextDescription>MRUN Reward:</ModalTextDescription>
              <ModalTextWrapper>
                {(yieldRate * amount) / 100 || 0} MRUN
              </ModalTextWrapper>
            </ModalText>
            <ModalText>
              <ModalTextDescription>Character NFT tokens:</ModalTextDescription>

              <ModalTextWrapper>
                {Math.floor(
                  Number(amount) / ethers.utils.formatEther(mrunPerCharacter)
                ) &&
                Math.floor(
                  Number(amount) / ethers.utils.formatEther(mrunPerCharacter)
                ) !== Infinity
                  ? Math.floor(
                      Number(amount) /
                        ethers.utils.formatEther(mrunPerCharacter)
                    )
                  : 0}{' '}
                x {name}
              </ModalTextWrapper>
            </ModalText>
            <ModalButtonWrapper>
              <Button
                size='extralong'
                variant='contained'
                onClick={submitHandler}
                disabled={
                  Number(amount) > Number(mrunBalance) || !inputValidity
                }
              >
                Stake
              </Button>
            </ModalButtonWrapper>
          </ModalForm>
        )}
      </ModalContainer>
    </Modal>
  )
}
